import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import "./manageUsers.css";
import {
  FiUserPlus,
  FiEdit2,
  FiTrash2,
  FiX,
  FiUsers,
  FiSearch,
  FiBriefcase,
  FiMoreVertical,
  FiChevronLeft,
  FiPlus,
  FiInfo,
  FiRepeat,
} from "react-icons/fi";
import { useSelector } from "react-redux";
import { getTeams, postTeams } from "../../services/teamsAPI";
import { notify } from "../../../common/Helper/notification";

const SEARCH_DEBOUNCE_MS = 300;

/* Name rules: must start with a letter (no digits/special chars) and stay
   within a length the backend accepts. */
const NAME_START_REGEX = /^[A-Za-z]/;
const MAX_NAME_LENGTH = 50;
// An agent's first and last names are stored together as a single "First Last"
// value in mn.Teams.AgentName (nvarchar(100)). Two 50-char fields can combine to
// 101 chars and overflow that column, so the *combined* name is capped here too.
const MAX_FULL_NAME_LENGTH = 100;

const validateNameValue = (value, label) => {
  const trimmed = (value || "").trim();
  if (!trimmed) return `${label} is required`;
  if (!NAME_START_REGEX.test(trimmed)) return `${label} must start with a letter`;
  if (trimmed.length > MAX_NAME_LENGTH)
    return `${label} must be ${MAX_NAME_LENGTH} characters or fewer`;
  return "";
};

// Guards the combined "First Last" length against the DB column size. Returns an
// empty string when the pieces are valid or incomplete (per-field validation
// covers those); only fires once both names are present but together too long.
const validateFullNameLength = (first, last) => {
  const full = `${(first || "").trim()} ${(last || "").trim()}`.trim();
  if (full.length > MAX_FULL_NAME_LENGTH)
    return `Full name must be ${MAX_FULL_NAME_LENGTH} characters or fewer`;
  return "";
};

// Email is optional — a member can have no address on file. When one is given
// it must be plausible: this address is where the person's call review
// feedback is sent, so a typo means it silently goes nowhere. Mirrors the
// check the Teams API applies server-side.
const MAX_EMAIL_LENGTH = 255;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateEmailValue = (value, { required = false } = {}) => {
  const trimmed = (value || "").trim();
  if (!trimmed)
    return required
      ? "Email is required so call feedback can reach this agent"
      : "";
  if (trimmed.length > MAX_EMAIL_LENGTH)
    return `Email must be ${MAX_EMAIL_LENGTH} characters or fewer`;
  if (!EMAIL_REGEX.test(trimmed)) return "Enter a valid email address";
  return "";
};

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length >= 2)
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0]?.[0] || "?").toUpperCase();
};

const getAvatarColor = (name) => {
  const colors = [
    "#1b3d5f",
    "#2d7d9a",
    "#e67e22",
    "#27ae60",
    "#8e44ad",
    "#c0392b",
    "#16a085",
    "#2980b9",
    "#d35400",
    "#6c5ce7",
  ];
  if (!name) return colors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash += name.charCodeAt(i);
  return colors[hash % colors.length];
};

const useDebouncedValue = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

/* Full roster: prefer the `members` array (every member + isActive flag,
   ordered active-first); fall back to `agents` (active-only names) for
   back-compat with older API responses. */
const getRoster = (team) => {
  if (Array.isArray(team?.members)) {
    return team.members.map((m) => ({
      name: m.agentName || m.name || "",
      // The address call review feedback is sent to. Blank for members whose
      // profile has never had one entered.
      email: (m.email || "").trim(),
      isActive: m.isActive !== false,
    }));
  }
  return (team?.agents || []).map((a) => ({
    name: typeof a === "string" ? a : a?.agentName || a?.name || "",
    isActive: true,
  }));
};

const getActiveCount = (team) =>
  Array.isArray(team?.agents)
    ? team.agents.length
    : getRoster(team).filter((m) => m.isActive).length;

/* Split "First Middle Last" -> first word / rest. */
const splitName = (fullName) => {
  const parts = (fullName || "").trim().split(/\s+/).filter(Boolean);
  return {
    first: parts[0] || "",
    last: parts.slice(1).join(" "),
  };
};

const EMPTY_FORM = { teamName: "", director: "", directorEmail: "" };

/* Panel modes for the right-side panel:
   "addMembers" (Add Agents — default when a team is selected),
   "addTeam", "editTeam", "editMember" (Edit Agent) */

const ManageUsersModal = ({ isOpen, onClose }) => {
  // Add/edit/remove is restricted to Admin, OPS Admin and Operations Manager;
  // Agents (and other roles) get a read-only view of teams and rosters.
  const userRole = useSelector(
    (state) => state.login?.login?.data?.data?.[0]?.Role,
  );
  const canManage =
    userRole === "Admin" ||
    userRole === "OPS_ADMIN" ||
    userRole === "Operations Manager";
  // The right-side "Add Agents" management panel is hidden for agents. This is
  // deliberately role-specific (only agents), not the inverse of canManage, so
  // any other non-manage role keeps its current read-only view unchanged.
  const isAgent = (userRole || "").toString().trim().toLowerCase() === "agent";

  const [teams, setTeams] = useState([]);
  const [teamNames, setTeamNames] = useState([]);
  const [directors, setDirectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [closing, setClosing] = useState(false);

  const [selectedTeamName, setSelectedTeamName] = useState(null);
  const [activeTab, setActiveTab] = useState("members");
  const [mobileView, setMobileView] = useState("list");

  const [listSearch, setListSearch] = useState("");
  const [memberSearch, setMemberSearch] = useState("");
  const debouncedListSearch = useDebouncedValue(listSearch, SEARCH_DEBOUNCE_MS);
  const debouncedMemberSearch = useDebouncedValue(
    memberSearch,
    SEARCH_DEBOUNCE_MS,
  );

  // Right panel
  const [panelMode, setPanelMode] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [touched, setTouched] = useState({});

  // Add-agent queue (names built from the first/last name fields below).
  // New agents are always created Active; status is managed via Edit Agent.
  // Queue entries are { name, email }. Email is optional and is what call
  // review feedback for that agent will be sent to.
  const [newAgents, setNewAgents] = useState([]);
  const [agentFirst, setAgentFirst] = useState("");
  const [agentLast, setAgentLast] = useState("");
  const [agentEmail, setAgentEmail] = useState("");

  // Edit Agent panel state. The original full name identifies the agent.
  const [editingMemberOriginal, setEditingMemberOriginal] = useState(null);
  const [memberFirstName, setMemberFirstName] = useState("");
  const [memberLastName, setMemberLastName] = useState("");
  // Where this person's call review feedback is sent. Held separately from the
  // value it loaded with so an untouched field is never written back.
  const [memberEmail, setMemberEmail] = useState("");
  const [memberOriginalEmail, setMemberOriginalEmail] = useState("");
  const [memberIsActive, setMemberIsActive] = useState(true);
  const [memberOriginalActive, setMemberOriginalActive] = useState(true);
  // "" = keep current team; otherwise the destination team for moveMember.
  const [memberTeamTarget, setMemberTeamTarget] = useState("");
  const [moveConfirmOpen, setMoveConfirmOpen] = useState(false);

  // Delete flows
  const [deleteTeamCandidate, setDeleteTeamCandidate] = useState(null);
  const [memberRemoveCandidate, setMemberRemoveCandidate] = useState(null);
  const [menuOpenFor, setMenuOpenFor] = useState(null);

  const modalRef = useRef(null);
  const teamListRef = useRef(null);

  const selectedTeam = useMemo(
    () => teams.find((t) => t.teamName === selectedTeamName) || null,
    [teams, selectedTeamName],
  );

  // Effective right-panel mode: with a team selected the panel defaults to
  // agent management so everything happens inline without extra popups.
  const effectiveMode =
    panelMode || (selectedTeam && canManage ? "addMembers" : null);

  const fetchTeams = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getTeams();
      setTeams(Array.isArray(data?.teams) ? data.teams : []);
      setTeamNames(Array.isArray(data?.teamNames) ? data.teamNames : []);
      setDirectors(Array.isArray(data?.directors) ? data.directors : []);
    } catch (error) {
      console.error("Failed to fetch teams:", error);
      notify.error(error?.message || "Failed to load teams");
      setTeams([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset + load when the modal opens fresh.
  useEffect(() => {
    if (!isOpen) return;
    setSelectedTeamName(null);
    setActiveTab("members");
    setMobileView("list");
    setListSearch("");
    setMemberSearch("");
    setPanelMode(null);
    setDrawerOpen(false);
    setForm(EMPTY_FORM);
    setNewAgents([]);
    setAgentFirst("");
    setAgentLast("");
    setAgentEmail("");
    setTouched({});
    setEditingMemberOriginal(null);
    setMoveConfirmOpen(false);
    setDeleteTeamCandidate(null);
    setMemberRemoveCandidate(null);
    setMenuOpenFor(null);
    setClosing(false);
    fetchTeams();
  }, [isOpen, fetchTeams]);

  // Auto-select the first team once loaded (desktop convenience).
  useEffect(() => {
    if (!loading && teams.length > 0 && !selectedTeamName) {
      setSelectedTeamName(teams[0].teamName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, teams]);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      onClose();
    }, 180);
  }, [onClose]);

  // ESC (confirmations first, then drawer, then modal) + focus trap.
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        if (moveConfirmOpen) setMoveConfirmOpen(false);
        else if (deleteTeamCandidate) setDeleteTeamCandidate(null);
        else if (memberRemoveCandidate) setMemberRemoveCandidate(null);
        else if (menuOpenFor) setMenuOpenFor(null);
        else if (drawerOpen || panelMode) {
          setDrawerOpen(false);
          setPanelMode(null);
        } else handleClose();
        return;
      }

      if (e.key === "Tab" && modalRef.current) {
        const focusables = modalRef.current.querySelectorAll(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [
    isOpen,
    moveConfirmOpen,
    deleteTeamCandidate,
    memberRemoveCandidate,
    menuOpenFor,
    drawerOpen,
    panelMode,
    handleClose,
  ]);

  // Close the three-dot menu on outside click.
  useEffect(() => {
    if (!menuOpenFor) return;
    const closeMenu = () => setMenuOpenFor(null);
    document.addEventListener("mousedown", closeMenu);
    return () => document.removeEventListener("mousedown", closeMenu);
  }, [menuOpenFor]);

  if (!isOpen) return null;

  /* ── Derived data ── */

  const listQuery = debouncedListSearch.trim().toLowerCase();
  const visibleTeams = listQuery
    ? teams.filter(
        (team) =>
          team.teamName?.toLowerCase().includes(listQuery) ||
          team.director?.toLowerCase().includes(listQuery) ||
          getRoster(team).some((m) =>
            m.name.toLowerCase().includes(listQuery),
          ),
      )
    : teams;

  const roster = getRoster(selectedTeam);
  const activeCount = roster.filter((m) => m.isActive).length;
  const inactiveCount = roster.length - activeCount;

  const memberQuery = debouncedMemberSearch.trim().toLowerCase();
  const visibleMembers = memberQuery
    ? roster.filter((m) => m.name.toLowerCase().includes(memberQuery))
    : roster;

  // Destination options for Change Team (per API: teamNames minus current).
  const moveTargets = (
    teamNames.length > 0 ? teamNames : teams.map((t) => t.teamName)
  ).filter((name) => name !== selectedTeamName);

  const errors = {
    teamName: validateNameValue(form.teamName, "Team name"),
    director: validateNameValue(form.director, "Director"),
    directorEmail: validateEmailValue(form.directorEmail),
  };

  /* Add-agent form: both names typed (and valid) but not yet queued still
     count towards the submit. */
  const pendingAgentFullNameError = validateFullNameLength(agentFirst, agentLast);
  // Required when adding: a member saved without an address is unreachable
  // for call review feedback, which is the whole point of collecting it.
  // Editing an existing member keeps it optional, so the members who predate
  // this field can still have their name, status or team changed.
  const pendingAgentEmailError = validateEmailValue(agentEmail, {
    required: true,
  });
  const pendingAgentValid =
    !validateNameValue(agentFirst, "First name") &&
    !validateNameValue(agentLast, "Last name") &&
    !pendingAgentFullNameError &&
    !pendingAgentEmailError;
  const pendingAgentName = pendingAgentValid
    ? `${agentFirst.trim()} ${agentLast.trim()}`
    : "";
  const agentsToAdd = pendingAgentName
    ? [...newAgents, { name: pendingAgentName, email: agentEmail.trim() }]
    : newAgents;

  const addAgentErrors = {
    email: touched.agentEmail ? pendingAgentEmailError : "",
    first: touched.agentFirst
      ? validateNameValue(agentFirst, "First name")
      : "",
    last: touched.agentLast ? validateNameValue(agentLast, "Last name") : "",
    // Combined-length error surfaces only once both fields are individually
    // valid (otherwise the per-field messages already explain what's wrong).
    full:
      (touched.agentFirst || touched.agentLast) &&
      !validateNameValue(agentFirst, "First name") &&
      !validateNameValue(agentLast, "Last name")
        ? pendingAgentFullNameError
        : "",
  };

  /* Edit Agent derived state */
  const memberNewFullName =
    `${memberFirstName.trim()} ${memberLastName.trim()}`.trim();
  const memberNameChanged =
    memberNewFullName !== (editingMemberOriginal || "");
  const memberStatusChanged = memberIsActive !== memberOriginalActive;
  const memberTeamChanged = Boolean(memberTeamTarget);

  const memberNameCollision = memberNameChanged
    ? roster.some(
        (m) =>
          m.name.toLowerCase() === memberNewFullName.toLowerCase() &&
          m.name !== editingMemberOriginal,
      )
    : false;

  const memberFirstNameError = validateNameValue(memberFirstName, "First name");
  const memberLastNameError = validateNameValue(memberLastName, "Last name");
  const memberFullNameError =
    !memberFirstNameError && !memberLastNameError
      ? validateFullNameLength(memberFirstName, memberLastName)
      : "";

  // Empty is allowed and means "no address on file"; anything present has to
  // be a plausible address, since a typo here silently sends someone's call
  // feedback nowhere.
  const memberEmailChanged =
    memberEmail.trim() !== (memberOriginalEmail || "").trim();
  const memberEmailError = validateEmailValue(memberEmail);

  const memberErrors = {
    firstName: touched.firstName ? memberFirstNameError : "",
    lastName: touched.lastName ? memberLastNameError : "",
    email: touched.email ? memberEmailError : "",
    full: memberFullNameError,
    collision: memberNameCollision
      ? "An agent with this name is already on the team"
      : "",
  };

  const panelIsValid = (() => {
    if (saving) return false;
    if (effectiveMode === "addTeam")
      return !errors.teamName && !errors.director && !errors.directorEmail;
    if (effectiveMode === "editTeam") {
      if (errors.teamName || errors.director || errors.directorEmail)
        return false;
      return (
        form.teamName.trim() !== selectedTeamName ||
        form.director.trim() !== (selectedTeam?.director || "") ||
        form.directorEmail.trim() !== (selectedTeam?.directorEmail || "")
      );
    }
    if (effectiveMode === "addMembers") return agentsToAdd.length > 0;
    if (effectiveMode === "editMember") {
      if (memberFirstNameError || memberLastNameError || memberFullNameError)
        return false;
      if (memberEmailError) return false;
      if (memberNameCollision) return false;
      return (
        memberNameChanged ||
        memberStatusChanged ||
        memberTeamChanged ||
        memberEmailChanged
      );
    }
    return false;
  })();

  /* ── Panel openers ── */

  // Clears every field the right-hand panel owns. Email belongs here with the
  // names: leaving it behind meant the next agent you added arrived carrying
  // the previous one's address, and since it looks like a filled-in field
  // rather than a stale one, it saves without anyone noticing.
  const resetPanelForm = () => {
    setForm(EMPTY_FORM);
    setNewAgents([]);
    setAgentFirst("");
    setAgentLast("");
    setAgentEmail("");
    setTouched({});
  };

  const openAddTeam = () => {
    resetPanelForm();
    setPanelMode("addTeam");
    setDrawerOpen(true);
  };

  const openEditTeam = (team) => {
    resetPanelForm();
    setForm({
      teamName: team.teamName,
      director: team.director || "",
      directorEmail: team.directorEmail || "",
    });
    setSelectedTeamName(team.teamName);
    setPanelMode("editTeam");
    setDrawerOpen(true);
    setMenuOpenFor(null);
  };

  const openAddMembers = () => {
    resetPanelForm();
    setPanelMode("addMembers");
    setDrawerOpen(true);
  };

  const openEditMember = (member) => {
    resetPanelForm();
    const { first, last } = splitName(member.name);
    setEditingMemberOriginal(member.name);
    setMemberFirstName(first);
    setMemberLastName(last);
    setMemberEmail(member.email || "");
    setMemberOriginalEmail(member.email || "");
    setMemberIsActive(member.isActive);
    setMemberOriginalActive(member.isActive);
    setMemberTeamTarget("");
    setMoveConfirmOpen(false);
    setPanelMode("editMember");
    setDrawerOpen(true);
    setMemberRemoveCandidate(null);
  };

  const closePanel = () => {
    setPanelMode(null);
    setDrawerOpen(false);
    setEditingMemberOriginal(null);
    setMoveConfirmOpen(false);
    resetPanelForm();
  };

  const handleSelectTeam = (team) => {
    if (team.teamName !== selectedTeamName) {
      setSelectedTeamName(team.teamName);
      setMemberSearch("");
      setActiveTab("members");
      setMemberRemoveCandidate(null);
      // Keep an explicit add/edit panel open only if it concerns the new team.
      if (panelMode === "editTeam" || panelMode === "editMember")
        setPanelMode(null);
    }
    setMobileView("detail");
  };

  /* ── Form helpers ── */

  const handleFieldChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleBlur = (field) => () =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  /* Queue one agent from the first/last fields (validated). */
  const commitAgentEntry = () => {
    if (
      validateNameValue(agentFirst, "First name") ||
      validateNameValue(agentLast, "Last name") ||
      validateFullNameLength(agentFirst, agentLast) ||
      validateEmailValue(agentEmail, { required: true })
    ) {
      setTouched((prev) => ({
        ...prev,
        agentFirst: true,
        agentLast: true,
        agentEmail: true,
      }));
      return;
    }
    const name = `${agentFirst.trim()} ${agentLast.trim()}`;
    const lower = name.toLowerCase();

    // Only an ACTIVE duplicate is blocked — re-adding an inactive member
    // reactivates them server-side.
    const activeDuplicate =
      effectiveMode === "addMembers" &&
      roster.some((m) => m.isActive && m.name.toLowerCase() === lower);
    const queuedDuplicate = newAgents.some(
      (a) => a.name.toLowerCase() === lower,
    );
    if (activeDuplicate || queuedDuplicate) {
      notify.info(`"${name}" is already on this team`);
      return;
    }
    const wasInactive =
      effectiveMode === "addMembers" &&
      roster.some((m) => !m.isActive && m.name.toLowerCase() === lower);
    if (wasInactive) notify.info(`"${name}" will be reactivated`);

    setNewAgents((prev) => [...prev, { name, email: agentEmail.trim() }]);
    setAgentFirst("");
    setAgentLast("");
    setAgentEmail("");
    setTouched((prev) => ({
      ...prev,
      agentFirst: false,
      agentLast: false,
      agentEmail: false,
    }));
  };

  const handleAgentFieldKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitAgentEntry();
    }
  };

  /* ── API operations ── */

  /* Adds agents (always Active; status is managed via Edit Agent). */
  const addAgentsToTeam = async (teamName, names) => {
    const response = await postTeams({
      action: "addMembers",
      teamName,
      agents: names,
    });
    if (response?.success === false) {
      notify.error(response?.message || "Failed to add agents");
      return false;
    }
    if (Array.isArray(response?.skipped) && response.skipped.length > 0) {
      notify.info(`Already on the team: ${response.skipped.join(", ")}`);
    }
    if (
      Array.isArray(response?.reactivated) &&
      response.reactivated.length > 0
    ) {
      notify.info(`Reactivated: ${response.reactivated.join(", ")}`);
    }
    return true;
  };

  /* Executes the Edit Agent save: rename, status and/or move — all applied
     together when the user presses Save. */
  const performEditMemberSave = async () => {
    let currentName = editingMemberOriginal;

    setSaving(true);
    try {
      // 1) Rename on the current team.
      if (memberNameChanged) {
        const response = await postTeams({
          action: "renameMember",
          teamName: selectedTeamName,
          agent: editingMemberOriginal,
          newAgentName: memberNewFullName,
        });
        if (response?.success === false) {
          notify.error(response?.message || "Failed to rename agent");
          return;
        }
        currentName = memberNewFullName;
        // The rename is committed. Re-anchor the panel to the new name so a
        // retry after a later step fails still targets the right member.
        setEditingMemberOriginal(memberNewFullName);
      }

      // 2) Move: one atomic call — deactivates on the source team (history
      //    preserved) and creates/reactivates an active row on the target.
      const finalTeam = memberTeamChanged
        ? memberTeamTarget
        : selectedTeamName;
      if (memberTeamChanged) {
        const response = await postTeams({
          action: "moveMember",
          teamName: selectedTeamName,
          agent: currentName,
          newTeamName: memberTeamTarget,
        });
        if (response?.success === false) {
          notify.error(response?.message || "Failed to move agent");
          return;
        }
        // Committed — re-anchor to the destination team for the same reason.
        setSelectedTeamName(memberTeamTarget);
        setMemberTeamTarget("");
      }

      // 3) Status is applied on Save (not while toggling). After a move the
      //    agent is active on the target, so only call when it differs.
      const statusNeedsApply = memberTeamChanged
        ? memberIsActive === false
        : memberStatusChanged;
      // Steps past this point are reported together at the end. Anything that
      // fails here has NOT stopped the earlier steps, which are already
      // committed — so the outcome is described accurately rather than
      // announcing a blanket success over the top of an error.
      const failures = [];

      if (statusNeedsApply) {
        const response = await postTeams({
          action: "setMemberStatus",
          teamName: finalTeam,
          agent: currentName,
          isActive: memberIsActive,
        });
        if (response?.success === false) {
          failures.push(response?.message || "Failed to update status");
        }
      }

      // 4) Email, last: it has to target the name and team the member ends up
      //    with, so it runs after any rename and move rather than before.
      //    Failure is surfaced but doesn't discard the rest of the save —
      //    the rename and move have already been committed by this point.
      if (memberEmailChanged) {
        const response = await postTeams({
          action: "updateMemberEmail",
          teamName: finalTeam,
          agent: currentName,
          email: memberEmail.trim(),
        });
        if (response?.success === false) {
          failures.push(response?.message || "Failed to update email");
        }
      }

      // A rejected email (already assigned to someone else) previously showed
      // its error AND "Agent Updated" at the same time, which left the user
      // unable to tell whether anything had saved. Only one outcome is
      // reported now, and the panel stays open on failure so the address can
      // be corrected instead of being lost with the dialog.
      if (failures.length) {
        failures.forEach((message) => notify.error(message));
        await fetchTeams(); // whatever did save is reflected in the list
        return;
      }

      notify.success(
        memberTeamChanged ? `Agent moved to ${finalTeam}` : "Agent Updated",
      );
      closePanel();
      await fetchTeams();
    } catch (error) {
      console.error("Failed to update agent:", error);
      notify.error(
        error?.response?.data?.message || error?.message || "Request failed",
      );
    } finally {
      setSaving(false);
      setMoveConfirmOpen(false);
    }
  };

  const submitPanel = async () => {
    if (!panelIsValid) return;

    // Team change is confirmed before anything is saved.
    if (effectiveMode === "editMember") {
      if (memberTeamChanged && !moveConfirmOpen) {
        setMoveConfirmOpen(true);
        return;
      }
      await performEditMemberSave();
      return;
    }

    const teamName = form.teamName.trim();
    const director = form.director.trim();

    setSaving(true);
    try {
      if (effectiveMode === "addTeam") {
        const response = await postTeams({
          action: "addTeam",
          teamName,
          director,
          directorEmail: form.directorEmail.trim(),
          agents: agentsToAdd,
        });
        if (response?.success === false) {
          notify.error(response?.message || "Failed to create team");
          return;
        }
        notify.success("Team Created");
        closePanel();
        await fetchTeams();
        setSelectedTeamName(teamName);
        setMobileView("detail");
      } else if (effectiveMode === "editTeam") {
        const renamed = teamName !== selectedTeamName;
        const directorChanged = director !== (selectedTeam?.director || "");
        const directorEmail = form.directorEmail.trim();
        const directorEmailChanged =
          directorEmail !== (selectedTeam?.directorEmail || "");
        const payload = { action: "updateTeam", teamName: selectedTeamName };
        if (renamed) payload.newTeamName = teamName;
        if (directorChanged) payload.director = director;
        // Sent only when it actually changed: the API treats an absent field
        // as "leave alone" and a blank string as "clear it".
        if (directorEmailChanged) payload.directorEmail = directorEmail;

        const response = await postTeams(payload);
        if (response?.success === false) {
          notify.error(response?.message || "Failed to update team");
          return;
        }
        notify.success("Team Updated");
        closePanel();
        await fetchTeams();
        // Preserve selection across the rename.
        setSelectedTeamName(teamName);
      } else if (effectiveMode === "addMembers") {
        const ok = await addAgentsToTeam(selectedTeamName, agentsToAdd);
        if (!ok) return;
        notify.success(
          agentsToAdd.length > 1 ? "Agents Added" : "Agent Added",
        );
        resetPanelForm();
        setDrawerOpen(false);
        setPanelMode(null);
        await fetchTeams();
      }
    } catch (error) {
      console.error("Team operation failed:", error);
      notify.error(
        error?.response?.data?.message || error?.message || "Request failed",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDeleteTeam = async () => {
    if (!deleteTeamCandidate) return;
    setDeleting(true);
    try {
      const response = await postTeams({
        action: "deleteTeam",
        teamName: deleteTeamCandidate.teamName,
      });
      if (response?.success === false) {
        notify.error(response?.message || "Failed to delete team");
        return;
      }
      notify.success("Team Deleted");
      if (selectedTeamName === deleteTeamCandidate.teamName) {
        setSelectedTeamName(null);
        setMobileView("list");
        closePanel();
      }
      setDeleteTeamCandidate(null);
      await fetchTeams();
    } catch (error) {
      console.error("Failed to delete team:", error);
      notify.error(
        error?.response?.data?.message || error?.message || "Request failed",
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleConfirmRemoveMember = async () => {
    if (!memberRemoveCandidate || !selectedTeamName) return;
    setDeleting(true);
    try {
      const response = await postTeams({
        action: "removeMembers",
        teamName: selectedTeamName,
        agents: [memberRemoveCandidate],
      });
      if (response?.success === false) {
        notify.error(response?.message || "Failed to remove agent");
        return;
      }
      if (Array.isArray(response?.notFound) && response.notFound.length > 0) {
        notify.info(`Not on the team: ${response.notFound.join(", ")}`);
      }
      notify.success("Agent Removed");
      setMemberRemoveCandidate(null);
      await fetchTeams();
    } catch (error) {
      console.error("Failed to remove agent:", error);
      notify.error(
        error?.response?.data?.message || error?.message || "Request failed",
      );
    } finally {
      setDeleting(false);
    }
  };

  /* ── Render helpers ── */

  const renderSkeletons = () => (
    <div className="mu-skeleton-list" aria-live="polite" aria-busy="true">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="mu-skel-card">
          <span className="mu-skel mu-skel-avatar" />
          <span className="mu-skel-lines">
            <span className="mu-skel mu-skel-line" style={{ width: "60%" }} />
            <span className="mu-skel mu-skel-line" style={{ width: "40%" }} />
          </span>
        </div>
      ))}
    </div>
  );

  const panelTitle =
    effectiveMode === "addTeam"
      ? "Create Team"
      : effectiveMode === "editTeam"
        ? "Edit Team"
        : effectiveMode === "editMember"
          ? "Edit Agent"
          : "Add Agents";

  const panelSubmitLabel =
    effectiveMode === "addTeam"
      ? "Create Team"
      : effectiveMode === "editTeam"
        ? "Save Changes"
        : effectiveMode === "editMember"
          ? memberTeamChanged
            ? "Save & Move"
            : "Save Agent"
          : "Add Agents";

  /* Shared agent-entry form (Add Team + Add Agents modes). */
  const renderAgentEntryForm = () => (
    <div className="mu-field">
      <span className="mu-label">
        {effectiveMode === "addTeam" ? "Agents" : "New Agents"}
      </span>

      <div className="mu-agent-form">
        <div className="mu-agent-form-names">
          <div className="mu-agent-form-col">
            <input
              id="mu-agent-first"
              type="text"
              className={`mu-input ${addAgentErrors.first ? "mu-input-error" : ""}`}
              placeholder="First name *"
              value={agentFirst}
              onChange={(e) => setAgentFirst(e.target.value)}
              onBlur={handleBlur("agentFirst")}
              onKeyDown={handleAgentFieldKeyDown}
              aria-label="Agent first name"
              aria-invalid={Boolean(addAgentErrors.first)}
              maxLength={MAX_NAME_LENGTH}
              disabled={saving}
            />
            {addAgentErrors.first && (
              <span className="mu-error-msg" role="alert">
                {addAgentErrors.first}
              </span>
            )}
          </div>
          <div className="mu-agent-form-col">
            <input
              id="mu-agent-last"
              type="text"
              className={`mu-input ${addAgentErrors.last ? "mu-input-error" : ""}`}
              placeholder="Last name *"
              value={agentLast}
              onChange={(e) => setAgentLast(e.target.value)}
              onBlur={handleBlur("agentLast")}
              onKeyDown={handleAgentFieldKeyDown}
              aria-label="Agent last name"
              aria-invalid={Boolean(addAgentErrors.last)}
              maxLength={MAX_NAME_LENGTH}
              disabled={saving}
            />
            {addAgentErrors.last && (
              <span className="mu-error-msg" role="alert">
                {addAgentErrors.last}
              </span>
            )}
          </div>
        </div>

        {addAgentErrors.full && (
          <span className="mu-error-msg" role="alert">
            {addAgentErrors.full}
          </span>
        )}

        {/* Full width rather than beside the names: an address is far longer
            than either name and would be clipped in a half-width column. */}
        <div className="mu-agent-form-row">
          <div className="mu-agent-form-col mu-agent-form-col-wide">
            <input
              id="mu-agent-email"
              type="email"
              className={`mu-input ${addAgentErrors.email ? "mu-input-error" : ""}`}
              placeholder="Email *"
              value={agentEmail}
              onChange={(e) => setAgentEmail(e.target.value)}
              onBlur={handleBlur("agentEmail")}
              onKeyDown={handleAgentFieldKeyDown}
              aria-label="Agent email"
              aria-invalid={Boolean(addAgentErrors.email)}
              maxLength={MAX_EMAIL_LENGTH}
              autoComplete="off"
              spellCheck="false"
              disabled={saving}
            />
            {addAgentErrors.email && (
              <span className="mu-error-msg" role="alert">
                {addAgentErrors.email}
              </span>
            )}
          </div>
        </div>

        <div className="mu-agent-form-row">
          <span className="mu-hint">
            Names must start with a letter. New agents are added as Active.
            Without an email they cannot receive call feedback, so one is
            required.
          </span>
          <button
            type="button"
            className="mu-btn-outline mu-agent-add-btn"
            onClick={commitAgentEntry}
            disabled={saving}
            title="Add this agent to the list"
          >
            <FiPlus aria-hidden="true" /> Add
          </button>
        </div>
      </div>

      <span className="mu-hint">
        First name, last name and email are all required. Call review feedback
        for this agent is sent to the address you enter here.
        {effectiveMode === "addMembers" &&
          " Re-adding an inactive agent reactivates them."}
      </span>

      {newAgents.length > 0 && (
        <div className="mu-agents-box mu-agents-queue">
          {newAgents.map((agent, index) => (
            <span
              key={`${agent.name}-${index}`}
              className="mu-chip mu-chip-new"
              // The address is on the chip's tooltip rather than its face —
              // it would double the chip width and the name is what people
              // scan the queue for.
              title={
                agent.email ? `${agent.name} · ${agent.email}` : agent.name
              }
            >
              {agent.name}
              <button
                type="button"
                className="mu-chip-remove"
                onClick={() =>
                  setNewAgents((prev) => prev.filter((_, i) => i !== index))
                }
                aria-label={`Remove ${agent.name} from list`}
                disabled={saving}
              >
                <FiX />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );

  const renderRightPanelBody = () => {
    if (!effectiveMode) {
      return (
        <div className="mu-panel-empty">
          <FiUsers aria-hidden="true" />
          <p>Select a team or create a new one to manage agents.</p>
        </div>
      );
    }

    return (
      <>
        {(effectiveMode === "addTeam" || effectiveMode === "editTeam") && (
          <>
            <div className="mu-field">
              <label className="mu-label" htmlFor="mu-team-name">
                Team Name <span className="mu-required">*</span>
              </label>
              <input
                id="mu-team-name"
                type="text"
                className={`mu-input ${touched.teamName && errors.teamName ? "mu-input-error" : ""}`}
                placeholder="e.g. Team Nova"
                value={form.teamName}
                onChange={handleFieldChange("teamName")}
                onBlur={handleBlur("teamName")}
                aria-invalid={Boolean(touched.teamName && errors.teamName)}
                maxLength={MAX_NAME_LENGTH}
                disabled={saving}
              />
              {touched.teamName && errors.teamName && (
                <span className="mu-error-msg" role="alert">
                  {errors.teamName}
                </span>
              )}
            </div>

            <div className="mu-field">
              <label className="mu-label" htmlFor="mu-director">
                Director <span className="mu-required">*</span>
              </label>
              <input
                id="mu-director"
                type="text"
                list="mu-director-options"
                className={`mu-input ${touched.director && errors.director ? "mu-input-error" : ""}`}
                placeholder="Enter or pick a director"
                value={form.director}
                onChange={handleFieldChange("director")}
                onBlur={handleBlur("director")}
                aria-invalid={Boolean(touched.director && errors.director)}
                maxLength={MAX_NAME_LENGTH}
                disabled={saving}
              />
              <datalist id="mu-director-options">
                {directors.map((director) => (
                  <option key={director} value={director} />
                ))}
              </datalist>
              {touched.director && errors.director && (
                <span className="mu-error-msg" role="alert">
                  {errors.director}
                </span>
              )}
            </div>

            <div className="mu-field">
              <label className="mu-label" htmlFor="mu-director-email">
                Director Email
              </label>
              <input
                id="mu-director-email"
                type="email"
                className={`mu-input ${touched.directorEmail && errors.directorEmail ? "mu-input-error" : ""}`}
                placeholder="name@montenido.com"
                value={form.directorEmail}
                onChange={handleFieldChange("directorEmail")}
                onBlur={handleBlur("directorEmail")}
                aria-invalid={Boolean(
                  touched.directorEmail && errors.directorEmail,
                )}
                aria-describedby="mu-director-email-hint"
                maxLength={MAX_EMAIL_LENGTH}
                autoComplete="off"
                spellCheck="false"
                disabled={saving}
              />
              {touched.directorEmail && errors.directorEmail && (
                <span className="mu-error-msg" role="alert">
                  {errors.directorEmail}
                </span>
              )}
              <span className="mu-hint" id="mu-director-email-hint">
                Optional. Used when sharing call feedback with the director.
              </span>
            </div>
          </>
        )}

        {effectiveMode === "editMember" && (
          <>
            <div className="mu-field">
              <label className="mu-label" htmlFor="mu-first-name">
                First Name <span className="mu-required">*</span>
              </label>
              <input
                id="mu-first-name"
                type="text"
                className={`mu-input ${memberErrors.firstName ? "mu-input-error" : ""}`}
                placeholder="First name"
                value={memberFirstName}
                onChange={(e) => setMemberFirstName(e.target.value)}
                onBlur={handleBlur("firstName")}
                aria-invalid={Boolean(memberErrors.firstName)}
                maxLength={MAX_NAME_LENGTH}
                disabled={saving}
              />
              {memberErrors.firstName && (
                <span className="mu-error-msg" role="alert">
                  {memberErrors.firstName}
                </span>
              )}
            </div>

            <div className="mu-field">
              <label className="mu-label" htmlFor="mu-last-name">
                Last Name <span className="mu-required">*</span>
              </label>
              <input
                id="mu-last-name"
                type="text"
                className={`mu-input ${memberErrors.lastName ? "mu-input-error" : ""}`}
                placeholder="Last name"
                value={memberLastName}
                onChange={(e) => setMemberLastName(e.target.value)}
                onBlur={handleBlur("lastName")}
                aria-invalid={Boolean(memberErrors.lastName)}
                maxLength={MAX_NAME_LENGTH}
                disabled={saving}
              />
              {memberErrors.lastName && (
                <span className="mu-error-msg" role="alert">
                  {memberErrors.lastName}
                </span>
              )}
              <span className="mu-hint">
                Please provide both the agent's first name and last name.
              </span>
            </div>

            {/* Optional, but it is the address call review feedback is sent
                to — so the hint says that rather than leaving the reader to
                guess what the field is for. */}
            <div className="mu-field">
              <label className="mu-label" htmlFor="mu-member-email">
                Email
              </label>
              <input
                id="mu-member-email"
                type="email"
                className={`mu-input ${memberErrors.email ? "mu-input-error" : ""}`}
                placeholder="name@montenido.com"
                value={memberEmail}
                onChange={(e) => setMemberEmail(e.target.value)}
                onBlur={handleBlur("email")}
                aria-invalid={Boolean(memberErrors.email)}
                aria-describedby="mu-member-email-hint"
                maxLength={MAX_EMAIL_LENGTH}
                autoComplete="off"
                spellCheck="false"
                disabled={saving}
              />
              {memberErrors.email && (
                <span className="mu-error-msg" role="alert">
                  {memberErrors.email}
                </span>
              )}
              <span className="mu-hint" id="mu-member-email-hint">
                Call review feedback for this agent is sent here. Leave blank if
                they should not receive it.
              </span>
            </div>

            {memberErrors.full && (
              <span className="mu-error-msg" role="alert">
                {memberErrors.full}
              </span>
            )}

            {memberErrors.collision && (
              <span className="mu-error-msg" role="alert">
                {memberErrors.collision}
              </span>
            )}

            {/* Status toggle — applied when Save is pressed */}
            <div className="mu-field">
              <span className="mu-label">Status</span>
              <div className="mu-status-row">
                <button
                  type="button"
                  role="switch"
                  aria-checked={memberIsActive}
                  className={`mu-switch ${memberIsActive ? "mu-switch-on" : ""}`}
                  onClick={() => setMemberIsActive((prev) => !prev)}
                  disabled={saving}
                  aria-label={`Set agent ${memberIsActive ? "inactive" : "active"}`}
                  title={
                    memberIsActive ? "Deactivate agent" : "Activate agent"
                  }
                >
                  <span className="mu-switch-thumb" />
                </button>
                <span
                  className={`mu-status-badge ${!memberIsActive ? "mu-status-inactive" : ""}`}
                >
                  {memberIsActive ? "Active" : "Inactive"}
                </span>
                {memberStatusChanged && (
                  <span className="mu-pending-note">unsaved</span>
                )}
              </div>
              <span className="mu-hint">
                The status change is applied when you press Save.
              </span>
            </div>

            {/* Change team — confirmed on Save via moveMember */}
            <div className="mu-field">
              <label className="mu-label" htmlFor="mu-agent-team">
                <FiRepeat aria-hidden="true" style={{ marginRight: 4 }} />
                Change Team
              </label>
              <select
                id="mu-agent-team"
                className="mu-input mu-select"
                value={memberTeamTarget}
                onChange={(e) => setMemberTeamTarget(e.target.value)}
                disabled={saving}
              >
                <option value="">Keep current team ({selectedTeamName})</option>
                {moveTargets.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
              {memberTeamChanged && (
                <span className="mu-hint">
                  Moving from <strong>{selectedTeamName}</strong> to{" "}
                  <strong>{memberTeamTarget}</strong> — you'll be asked to
                  confirm.
                </span>
              )}
            </div>

            <div className="mu-info-box">
              <div className="mu-info-box-title">
                <FiInfo aria-hidden="true" /> Deactivate vs. Remove
              </div>
              <div className="mu-info-section">
                <span className="mu-info-heading">Deactivate Agent</span>
                <ul>
                  <li>Agent cannot receive new assignments.</li>
                  <li>Historical reporting and metrics are preserved.</li>
                  <li>Agent can be reactivated later.</li>
                </ul>
              </div>
              <div className="mu-info-section">
                <span className="mu-info-heading">Remove Agent</span>
                <ul>
                  <li>Permanently removes the agent from the team.</li>
                  <li>May affect historical references.</li>
                </ul>
              </div>
            </div>
          </>
        )}

        {(effectiveMode === "addTeam" || effectiveMode === "addMembers") &&
          renderAgentEntryForm()}

        {effectiveMode === "addMembers" && selectedTeam && (
          <div className="mu-field">
            <span className="mu-label">
              Current Agents ({activeCount} active
              {inactiveCount > 0 ? `, ${inactiveCount} inactive` : ""})
            </span>
            <div className="mu-current-members">
              {roster.length === 0 ? (
                <span className="mu-hint">No agents yet.</span>
              ) : (
                roster.map((member) => (
                  <span
                    key={member.name}
                    className={`mu-chip ${!member.isActive ? "mu-chip-inactive" : ""}`}
                    title={`${member.name}${member.isActive ? "" : " (Inactive)"}`}
                  >
                    {member.name}
                  </span>
                ))
              )}
            </div>
          </div>
        )}

        <div className="mu-panel-actions">
          <button
            type="button"
            className="mu-btn-secondary"
            onClick={closePanel}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="mu-btn-primary"
            onClick={submitPanel}
            disabled={!panelIsValid}
          >
            {saving && <span className="mu-btn-loader" aria-hidden="true" />}
            {panelSubmitLabel}
          </button>
        </div>
      </>
    );
  };

  return (
    <div
      className={`mu-overlay ${closing ? "mu-closing" : ""}`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
      role="presentation"
    >
      <div
        className={`mu-modal ${closing ? "mu-closing" : ""}`}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mu-modal-title"
      >
        {/* ════ Header ════ */}
        <div className="mu-header">
          <div className="mu-header-title-wrap">
            <span className="mu-header-icon-badge" aria-hidden="true">
              <FiUsers />
            </span>
            <div>
              <h2 id="mu-modal-title" className="mu-title">
                Manage Teams
              </h2>
              <p className="mu-subtitle">
                Browse teams, manage agents and edit team details
              </p>
            </div>
          </div>
          <button
            type="button"
            className="mu-close-btn"
            onClick={handleClose}
            aria-label="Close manage teams dialog"
            title="Close"
          >
            <FiX />
          </button>
        </div>

        {/* ════ Body: three panels ════ */}
        <div
          className={`mu-body mu-view-${mobileView} ${drawerOpen ? "mu-drawer-open" : ""}`}
        >
          {/* ── LEFT: team list ── */}
          <aside className="mu-teams-panel">
            <div className="mu-panel-toolbar">
              <span className="mu-panel-title">
                Teams
                {!loading && (
                  <span className="mu-count-badge">{teams.length}</span>
                )}
              </span>
              {canManage && (
                <button
                  type="button"
                  className="mu-add-btn"
                  onClick={openAddTeam}
                  aria-label="Add a new team"
                  title="Add a new team"
                >
                  <FiPlus aria-hidden="true" />
                  Add Team
                </button>
              )}
            </div>

            <div className="mu-search-box">
              <FiSearch className="mu-search-icon" aria-hidden="true" />
              <input
                type="text"
                placeholder="Search teams"
                value={listSearch}
                onChange={(e) => setListSearch(e.target.value)}
                aria-label="Search teams"
              />
            </div>

            <div
              className="mu-team-list"
              ref={teamListRef}
              role="list"
              aria-label="Team list"
            >
              {loading ? (
                renderSkeletons()
              ) : visibleTeams.length === 0 ? (
                <div className="mu-state-block mu-empty">
                  <FiUsers className="mu-empty-icon" aria-hidden="true" />
                  <p className="mu-empty-title">
                    {teams.length === 0 ? "No teams yet" : "No matches"}
                  </p>
                  <p className="mu-empty-sub">
                    {teams.length === 0
                      ? "Create your first team."
                      : "Try a different search."}
                  </p>
                </div>
              ) : (
                visibleTeams.map((team) => (
                  <div
                    key={team.teamName}
                    role="listitem"
                    className={`mu-team-card ${selectedTeamName === team.teamName ? "mu-selected" : ""}`}
                    onClick={() => handleSelectTeam(team)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSelectTeam(team);
                      }
                    }}
                    tabIndex={0}
                    aria-label={`${team.teamName}, director ${team.director}`}
                  >
                    <span
                      className="mu-avatar"
                      style={{ backgroundColor: getAvatarColor(team.teamName) }}
                      aria-hidden="true"
                    >
                      {getInitials(team.teamName.replace(/^Team\s+/i, ""))}
                    </span>
                    <span className="mu-team-card-info">
                      <span
                        className="mu-team-card-name"
                        title={team.teamName}
                      >
                        {team.teamName}
                      </span>
                      <span
                        className="mu-team-card-director"
                        title={team.director || ""}
                      >
                        {team.director || "—"}
                      </span>
                    </span>
                    <span
                      className="mu-role-badge"
                      title={`${getActiveCount(team)} active agents`}
                    >
                      {getActiveCount(team)}
                    </span>
                    {canManage && (
                    <span className="mu-menu-wrap">
                      <button
                        type="button"
                        className="mu-icon-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpenFor(
                            menuOpenFor === team.teamName
                              ? null
                              : team.teamName,
                          );
                        }}
                        aria-label={`Team options for ${team.teamName}`}
                        aria-haspopup="menu"
                        aria-expanded={menuOpenFor === team.teamName}
                        title="Team options"
                      >
                        <FiMoreVertical />
                      </button>
                      {menuOpenFor === team.teamName && (
                        <div
                          className="mu-menu"
                          role="menu"
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            role="menuitem"
                            className="mu-menu-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditTeam(team);
                            }}
                          >
                            <FiEdit2 aria-hidden="true" /> Rename Team
                          </button>
                          {/* Delete Team disabled until the backend ships a deleteTeam action.
                          <button
                            type="button"
                            role="menuitem"
                            className="mu-menu-item mu-menu-item-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenuOpenFor(null);
                              setDeleteTeamCandidate(team);
                            }}
                          >
                            <FiTrash2 aria-hidden="true" /> Delete Team
                          </button>
                          */}
                        </div>
                      )}
                    </span>
                    )}
                  </div>
                ))
              )}
            </div>
          </aside>

          {/* ── CENTER: selected team ── */}
          <section className="mu-detail-panel">
            {loading ? (
              <div className="mu-detail-skeleton">{renderSkeletons()}</div>
            ) : !selectedTeam ? (
              <div className="mu-state-block mu-empty">
                <FiUsers className="mu-empty-icon" aria-hidden="true" />
                <p className="mu-empty-title">No team selected</p>
                <p className="mu-empty-sub">
                  Pick a team from the list to view its agents.
                </p>
              </div>
            ) : (
              <>
                <div className="mu-detail-header">
                  <button
                    type="button"
                    className="mu-back-btn"
                    onClick={() => setMobileView("list")}
                    aria-label="Back to team list"
                    title="Back to team list"
                  >
                    <FiChevronLeft />
                  </button>
                  <span
                    className="mu-avatar mu-avatar-lg"
                    style={{
                      backgroundColor: getAvatarColor(selectedTeam.teamName),
                    }}
                    aria-hidden="true"
                  >
                    {getInitials(
                      selectedTeam.teamName.replace(/^Team\s+/i, ""),
                    )}
                  </span>
                  <div className="mu-detail-head-info">
                    <h3
                      className="mu-detail-title"
                      title={selectedTeam.teamName}
                    >
                      {selectedTeam.teamName}
                    </h3>
                    <div className="mu-detail-meta">
                      <span className="mu-detail-meta-item">
                        <FiBriefcase aria-hidden="true" />
                        {selectedTeam.director || "—"}
                      </span>
                      <span className="mu-detail-meta-item">
                        <FiUsers aria-hidden="true" />
                        {activeCount} active
                        {inactiveCount > 0 && ` · ${inactiveCount} inactive`}
                      </span>
                    </div>
                  </div>
                  <div className="mu-detail-actions">
                    {canManage && (
                      <button
                        type="button"
                        className="mu-btn-outline"
                        onClick={() => openEditTeam(selectedTeam)}
                        title="Edit team name or director"
                      >
                        <FiEdit2 aria-hidden="true" /> Edit Team
                      </button>
                    )}
                    {/* Delete Team disabled until the backend ships a deleteTeam action.
                    <button
                      type="button"
                      className="mu-btn-outline mu-btn-outline-danger"
                      onClick={() => setDeleteTeamCandidate(selectedTeam)}
                    >
                      <FiTrash2 aria-hidden="true" /> Delete
                    </button>
                    */}
                  </div>
                </div>

                <div className="mu-tabs" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "members"}
                    className={`mu-tab ${activeTab === "members" ? "mu-tab-active" : ""}`}
                    onClick={() => setActiveTab("members")}
                  >
                    Agents
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "details"}
                    className={`mu-tab ${activeTab === "details" ? "mu-tab-active" : ""}`}
                    onClick={() => setActiveTab("details")}
                  >
                    Team Details
                  </button>
                </div>

                {activeTab === "members" ? (
                  <div className="mu-members-area">
                    <div className="mu-members-toolbar">
                      <div className="mu-search-box mu-search-sm">
                        <FiSearch
                          className="mu-search-icon"
                          aria-hidden="true"
                        />
                        <input
                          type="text"
                          placeholder="Search agents"
                          value={memberSearch}
                          onChange={(e) => setMemberSearch(e.target.value)}
                          aria-label="Search agents"
                        />
                      </div>
                      {canManage && (
                        <button
                          type="button"
                          className="mu-add-btn"
                          onClick={openAddMembers}
                          title="Add agents to this team"
                        >
                          <FiUserPlus aria-hidden="true" /> Add Agent
                        </button>
                      )}
                    </div>

                    <div
                      className={`mu-table-wrap ${visibleMembers.length === 0 ? "mu-table-wrap-empty" : ""}`}
                    >
                      {visibleMembers.length === 0 ? (
                        <div className="mu-state-block mu-empty">
                          <FiUsers
                            className="mu-empty-icon"
                            aria-hidden="true"
                          />
                          <p className="mu-empty-title">No agents found.</p>
                          <p className="mu-empty-sub">
                            {roster.length === 0
                              ? canManage
                                ? "Add agents with the button above."
                                : "This team has no agents yet."
                              : "Try a different search."}
                          </p>
                        </div>
                      ) : (
                        <table className="mu-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Role</th>
                              <th>Status</th>
                              {canManage && (
                                <th className="mu-th-actions">Actions</th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {visibleMembers.map((member) => (
                              <tr
                                key={member.name}
                                className={
                                  !member.isActive ? "mu-row-inactive" : ""
                                }
                              >
                                <td>
                                  <span
                                    className="mu-member-cell"
                                    title={member.name}
                                  >
                                    <span
                                      className="mu-avatar mu-avatar-sm"
                                      style={{
                                        backgroundColor: getAvatarColor(
                                          member.name,
                                        ),
                                      }}
                                      aria-hidden="true"
                                    >
                                      {getInitials(member.name)}
                                    </span>
                                    <span className="mu-member-name">
                                      {member.name}
                                    </span>
                                  </span>
                                </td>
                                <td>
                                  <span className="mu-role-badge">Agent</span>
                                </td>
                                <td>
                                  <span
                                    className={`mu-status-badge ${!member.isActive ? "mu-status-inactive" : ""}`}
                                  >
                                    {member.isActive ? "Active" : "Inactive"}
                                  </span>
                                </td>
                                {canManage && (
                                  <td className="mu-td-actions">
                                    <button
                                      type="button"
                                      className="mu-icon-btn"
                                      onClick={() => openEditMember(member)}
                                      aria-label={`Edit ${member.name}`}
                                      title="Edit agent"
                                    >
                                      <FiEdit2 />
                                    </button>
                                    <button
                                      type="button"
                                      className="mu-icon-btn mu-icon-btn-danger"
                                      onClick={() =>
                                        setMemberRemoveCandidate(member.name)
                                      }
                                      aria-label={`Remove ${member.name}`}
                                      title="Remove agent from team"
                                    >
                                      <FiTrash2 />
                                    </button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="mu-details-tab">
                    <dl className="mu-details-grid">
                      <div className="mu-details-item">
                        <dt>Team Name</dt>
                        <dd>{selectedTeam.teamName}</dd>
                      </div>
                      <div className="mu-details-item">
                        <dt>Director</dt>
                        <dd>{selectedTeam.director || "—"}</dd>
                      </div>
                      <div className="mu-details-item">
                        <dt>Active Agents</dt>
                        <dd>{activeCount}</dd>
                      </div>
                      <div className="mu-details-item">
                        <dt>Inactive Agents</dt>
                        <dd>{inactiveCount}</dd>
                      </div>
                    </dl>
                  </div>
                )}
              </>
            )}
          </section>

          {/* ── RIGHT: agent management / add-edit drawer ── */}
          {/* Hidden entirely for agents — they have no management actions, so the
              "Add Agents" panel (title + empty state) would just be dead UI. */}
          {!isAgent && (
            <aside
              className={`mu-right-panel ${drawerOpen || panelMode ? "mu-right-open" : ""}`}
              aria-label={panelTitle}
            >
              <div className="mu-panel-toolbar">
                <span className="mu-panel-title">{panelTitle}</span>
                {(drawerOpen || panelMode) && (
                  <button
                    type="button"
                    className="mu-icon-btn"
                    onClick={closePanel}
                    aria-label="Close panel"
                    title="Close panel"
                  >
                    <FiX />
                  </button>
                )}
              </div>
              <div className="mu-right-body">{renderRightPanelBody()}</div>
            </aside>
          )}
        </div>

        {/* ════ Delete team confirmation ════ */}
        {deleteTeamCandidate && (
          <div
            className="mu-confirm-overlay"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="mu-confirm-title"
          >
            <div className="mu-confirm-box">
              <span className="mu-confirm-icon" aria-hidden="true">
                <FiTrash2 />
              </span>
              <p id="mu-confirm-title" className="mu-confirm-text">
                Delete <strong>{deleteTeamCandidate.teamName}</strong>?
              </p>
              <p className="mu-confirm-sub">This action cannot be undone.</p>
              <div className="mu-confirm-actions">
                <button
                  type="button"
                  className="mu-btn-secondary"
                  onClick={() => setDeleteTeamCandidate(null)}
                  disabled={deleting}
                  autoFocus
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="mu-btn-danger"
                  onClick={handleConfirmDeleteTeam}
                  disabled={deleting}
                >
                  {deleting && (
                    <span className="mu-btn-loader" aria-hidden="true" />
                  )}
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ════ Remove-agent confirmation ════ */}
        {memberRemoveCandidate && (
          <div
            className="mu-confirm-overlay"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="mu-remove-confirm-title"
          >
            <div className="mu-confirm-box">
              <span className="mu-confirm-icon" aria-hidden="true">
                <FiTrash2 />
              </span>
              <p id="mu-remove-confirm-title" className="mu-confirm-text">
                Are you sure you want to delete{" "}
                <strong>{memberRemoveCandidate}</strong>?
              </p>
              <p className="mu-confirm-sub">
                The agent will be removed from {selectedTeamName}.
              </p>
              <div className="mu-confirm-actions">
                <button
                  type="button"
                  className="mu-btn-secondary"
                  onClick={() => setMemberRemoveCandidate(null)}
                  disabled={deleting}
                  autoFocus
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="mu-btn-danger"
                  onClick={handleConfirmRemoveMember}
                  disabled={deleting}
                >
                  {deleting && (
                    <span className="mu-btn-loader" aria-hidden="true" />
                  )}
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ════ Change-team confirmation ════ */}
        {moveConfirmOpen && (
          <div
            className="mu-confirm-overlay"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="mu-move-confirm-title"
          >
            <div className="mu-confirm-box">
              <span
                className="mu-confirm-icon mu-confirm-icon-info"
                aria-hidden="true"
              >
                <FiRepeat />
              </span>
              <p id="mu-move-confirm-title" className="mu-confirm-text">
                Move <strong>{memberNewFullName}</strong> to{" "}
                <strong>{memberTeamTarget}</strong>?
              </p>
              <p className="mu-confirm-sub">
                The agent becomes active on {memberTeamTarget}; their record on{" "}
                {selectedTeamName} is kept as inactive so historical metrics
                are preserved.
              </p>
              <div className="mu-confirm-actions">
                <button
                  type="button"
                  className="mu-btn-secondary"
                  onClick={() => setMoveConfirmOpen(false)}
                  disabled={saving}
                  autoFocus
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="mu-btn-primary"
                  onClick={performEditMemberSave}
                  disabled={saving}
                >
                  {saving && (
                    <span className="mu-btn-loader" aria-hidden="true" />
                  )}
                  Move Agent
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageUsersModal;
